import { ACCESS_LEVELS } from "../constants";
import { useRef, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/cn";

type Props<T extends string> = {
  value: T;
  onChange: (level: T) => void;
  options?: { value: T; label: string }[];
  compact?: boolean;
};

const AccessLevelTabs = <T extends string>({
  value,
  onChange,
  options = ACCESS_LEVELS as { value: T; label: string }[],
  compact = false,
}: Props<T>) => {
  const { t, i18n } = useTranslation();
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (options.length === 0) return;

    let nextIndex: number | undefined;
    const isRtl = i18n.dir() === "rtl";
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = options.length - 1;
    if (event.key === "ArrowDown") nextIndex = (index + 1) % options.length;
    if (event.key === "ArrowUp") {
      nextIndex = (index - 1 + options.length) % options.length;
    }
    if (event.key === "ArrowRight") {
      nextIndex = isRtl
        ? (index - 1 + options.length) % options.length
        : (index + 1) % options.length;
    }
    if (event.key === "ArrowLeft") {
      nextIndex = isRtl
        ? (index + 1) % options.length
        : (index - 1 + options.length) % options.length;
    }

    if (nextIndex === undefined) return;
    event.preventDefault();
    onChange(options[nextIndex].value);
    buttonRefs.current[nextIndex]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={t("mcpPage.fields.accessLevel")}
      className={cn(
        "inline-flex items-center gap-1 rounded-[10px] bg-secondary p-1",
        compact ? "h-9" : "h-10",
      )}
    >
      {options.map(({ value: level }, index) => {
        const isActive = level === value;
        return (
          <button
            key={level}
            ref={(element) => {
              buttonRefs.current[index] = element;
            }}
            type="button"
            role="radio"
            aria-checked={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(level)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              "rounded-[8px] px-4 text-sm outline-none transition-colors focus-visible:ring-3 focus-visible:ring-ring/50",
              compact ? "h-7" : "h-8",
              isActive
                ? "bg-background font-semibold text-foreground"
                : "font-medium text-secondary-foreground/70 hover:text-foreground",
            )}
          >
            {t(`mcpPage.accessLevels.${level}`)}
          </button>
        );
      })}
    </div>
  );
};

export default AccessLevelTabs;
